import { IOnRoute } from '../crud/types';

/**
 * Base interface for common LLM profile fields
 */
export interface ILLMProfileFields {
  description: string;
  model_id: string;
}

export interface ILLMProfilesState {
  llmProfiles: ILLMProfile[];
  currentLLMProfileId: string | null;
  showLLMProfileForm: boolean;
  llmProfileForm: {
    id: string;
    errors: Record<string, string>;
  } & ILLMProfileFields;
}

/**
 * Represents an llmProfile.
 */
export interface ILLMProfile extends IOnRoute, Partial<ILLMProfileFields> {
  profile_id: string;
  level: any;
  created_at?: string;
  updated_at?: string;
}

export type ICreateLLMProfileParams = Pick<ILLMProfile, 'profile_id'> & ILLMProfileFields;

// Takes required profile_id from ILLMProfile
// Takes the rest of the fields from ILLMProfileFields
// Makes all those fields optional with Partial
// This ensures:
//    profile_id is always required
//    All other fields are optional
export type IUpdateLLMProfileParams = Pick<ILLMProfile, 'profile_id'> & Partial<ILLMProfileFields>;

// LLMProfile-specific action types
export const SET_LLM_PROFILES = 'SET_LLM_PROFILES';
export const ADD_LLM_PROFILE = 'ADD_LLM_PROFILE';
export const SET_CURRENT_LLM_PROFILE = 'SET_CURRENT_LLM_PROFILE';
export const SET_LLM_PROFILE_CONFIRMED_BY_BACKEND = 'SET_LLM_PROFILE_CONFIRMED_BY_BACKEND';
export const SET_LLM_PROFILE_STATE = 'SET_LLM_PROFILE_STATE';
export const DELETE_LLM_PROFILE = 'DELETE_LLM_PROFILE';

// Form-specific action types
export const TOGGLE_LLM_PROFILE_FORM = 'TOGGLE_LLM_PROFILE_FORM';
export const UPDATE_LLM_PROFILE_FIELD = 'UPDATE_LLM_PROFILE_FIELD';
export const SET_LLM_PROFILE_FORM_ERRORS = 'SET_LLM_PROFILE_FORM_ERRORS';
export const RESET_LLM_PROFILE_FORM = 'RESET_LLM_PROFILE_FORM';

// LLMProfile-specific action interfaces
export interface IAddLLMProfileAction {
  type: typeof ADD_LLM_PROFILE;
  payload: ILLMProfile;
}

export interface ISetLLMProfilesAction {
  type: typeof SET_LLM_PROFILES;
  payload: ILLMProfile[];
}

export interface ISetCurrentLLMProfileAction {
  type: typeof SET_CURRENT_LLM_PROFILE;
  payload: string;
}

export interface ISetLLMProfileConfirmedByBackendAction {
  type: typeof SET_LLM_PROFILE_CONFIRMED_BY_BACKEND;
  profile_id: string;
  updated_at: string;
}

export interface ISetLLMProfileStateAction {
  type: typeof SET_LLM_PROFILE_STATE;
  payload: Partial<ILLMProfile> & { profile_id: string };
}

export interface IDeleteLLMProfileAction {
  type: typeof DELETE_LLM_PROFILE;
  payload: string;
}

// Form-specific action interfaces
export interface IToggleLLMProfileFormAction {
  type: typeof TOGGLE_LLM_PROFILE_FORM;
  payload: boolean;
}

export interface IUpdateLLMProfileFieldAction {
  type: typeof UPDATE_LLM_PROFILE_FIELD;
  payload: { field: string; value: string | number | boolean };
}

export interface ISetLLMProfileFormErrorsAction {
  type: typeof SET_LLM_PROFILE_FORM_ERRORS;
  payload: Record<string, string>;
}

export interface IResetLLMProfileFormAction {
  type: typeof RESET_LLM_PROFILE_FORM;
}

export type LLMProfileActionTypes =
  | IAddLLMProfileAction
  | ISetLLMProfilesAction
  | ISetCurrentLLMProfileAction
  | ISetLLMProfileConfirmedByBackendAction
  | ISetLLMProfileStateAction
  | IDeleteLLMProfileAction
  | IToggleLLMProfileFormAction
  | IUpdateLLMProfileFieldAction
  | ISetLLMProfileFormErrorsAction
  | IResetLLMProfileFormAction;
